import { builtinProgramCover } from './utils/programThemes';
import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers.Authorization === undefined) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// FastAPI returns `detail` as a string for HTTPException, but as an ARRAY of
// {type, loc, msg, input, ctx} objects for 422 request-validation failures.
// Call sites do `toast.error(err.response?.data?.detail || '...')`, which hands
// React an object and crashes the render, so flatten it to a readable string
// here. The original payload stays available as `data.detail_raw`.
const flattenDetail = (detail) => {
  if (Array.isArray(detail)) {
    return (
      detail
        .map((e) => {
          const field = Array.isArray(e?.loc)
            ? e.loc.filter((p) => p !== 'body' && p !== 'query').join('.')
            : '';
          const msg = e?.msg || e?.message || 'Invalid value';
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join('; ') || 'Invalid request'
    );
  }
  return detail?.msg || detail?.message || 'Invalid request';
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;
    if (data && typeof data === 'object' && data.detail && typeof data.detail !== 'string') {
      data.detail_raw = data.detail;
      data.detail = flattenDetail(data.detail);
    }
    // A 401 from a login endpoint means "wrong credentials" — let the login page
    // show that itself instead of bouncing to /login and swallowing the message.
    const isLoginAttempt = /\/auth\/(login|google)$/.test(error.config?.url || '');
    if (error.response?.status === 401 && !isLoginAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const fileUrl = (fileId) =>
  fileId ? (builtinProgramCover(fileId) || `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/public`) : null;

export const openAuthenticatedFile = async (fileId) => {
  if (!fileId) return;

  const response = await api.get(`/files/${fileId}`, {
    responseType: 'blob',
  });

  const blobUrl = window.URL.createObjectURL(response.data);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');

  // Give the new tab time to load the blob before releasing it.
  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 60000);
};

export default api;
